import { useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const damageLabels: Record<string, string> = {
  water: 'Water Damage',
  fire: 'Fire & Smoke Damage',
  mold: 'Mold & Mildew',
  storm: 'Storm & Wind Damage',
  pipe: 'Pipe Leak & Drainage',
  biohazard: 'Biohazard Cleanup',
  structural: 'Structural Damage',
  other: 'Other / Not Sure',
};

const urgencyLabels: Record<string, { label: string; color: string }> = {
  emergency: { label: 'Emergency — Happening Now', color: 'bg-red-100 text-red-700' },
  urgent: { label: 'Urgent — Within 24 Hours', color: 'bg-orange-100 text-orange-700' },
  soon: { label: 'Soon — This Week', color: 'bg-amber-100 text-amber-700' },
  scheduled: { label: 'Scheduled — Planning Ahead', color: 'bg-green-100 text-green-700' },
};

interface FilePreview {
  name: string;
  type: string;
  size: string;
  isVideo: boolean;
}

export default function CustomerInformationPage() {
  const [searchParams] = useSearchParams();
  const damageParam = searchParams.get('damage') || '';
  const urgencyParam = searchParams.get('urgency') || '';
  const selectedDamages = damageParam ? damageParam.split(',') : [];
  const urgencyInfo = urgencyLabels[urgencyParam];

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [filePreviews, setFilePreviews] = useState<FilePreview[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const previews: FilePreview[] = files.map((f) => ({
      name: f.name,
      type: f.type,
      size: f.size > 1024 * 1024 ? `${(f.size / (1024 * 1024)).toFixed(1)} MB` : `${(f.size / 1024).toFixed(0)} KB`,
      isVideo: f.type.startsWith('video/'),
    }));
    setFilePreviews(previews);
  };

  const removeFile = (idx: number) => {
    setFilePreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const desc = (form.elements.namedItem('loss_description') as HTMLTextAreaElement)?.value || '';
    if (desc.length > 500) return;

    setSubmitting(true);
    const formData = new URLSearchParams();
    const elements = form.elements;
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i] as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      if (el.name && el.value) {
        formData.append(el.name, el.value);
      }
    }
    if (selectedDamages.length > 0) {
      formData.append('damage_types', selectedDamages.map((d) => damageLabels[d] || d).join(', '));
    }
    if (urgencyParam) {
      formData.append('urgency', urgencyInfo?.label || urgencyParam);
    }
    if (filePreviews.length > 0) {
      formData.append('media_files', `${filePreviews.length} file(s) submitted: ${filePreviews.map((f) => f.name).join(', ')} (Uncollectable)`);
    }

    try {
      await fetch('https://readdy.ai/api/form/d7ckmg6m3fsfopf23fbg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-[#1E3A5F]">
        <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-8 text-center">
          <span className="inline-flex items-center gap-2 bg-[#DC2626] text-white text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-widest mb-4">
            <i className="ri-user-fill"></i>
            Step 2 of 2
          </span>
          <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            Tell Us About Your Loss
          </h1>
          <p className="text-white/70 text-sm max-w-lg mx-auto">
            Provide your contact details, describe the damage, and upload any photos or videos to help us prepare the right team for your property.
          </p>
        </div>
      </section>

      <section className="py-14 bg-[#F8F9FA]">
        <div className="max-w-3xl mx-auto px-4 md:px-8">

          {/* Progress */}
          <div className="flex items-center justify-center gap-3 mb-10">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-green-500 text-white text-xs font-bold">
                <i className="ri-check-line text-sm"></i>
              </div>
              <span className="text-sm text-gray-400">Damage Type</span>
            </div>
            <div className="w-16 h-px bg-gray-300"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#DC2626] text-white text-xs font-bold">2</div>
              <span className="text-sm font-semibold text-[#1E3A5F]">Your Information</span>
            </div>
          </div>

          {/* Selected Summary */}
          {(selectedDamages.length > 0 || urgencyParam) && (
            <div className="bg-white rounded-2xl p-5 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div>
                <p className="text-xs text-gray-400 font-medium mb-2">Selected Damage Types</p>
                <div className="flex flex-wrap gap-2">
                  {selectedDamages.map((id) => (
                    <span key={id} className="bg-[#1E3A5F]/10 text-[#1E3A5F] text-xs font-medium px-3 py-1 rounded-full">
                      {damageLabels[id] || id}
                    </span>
                  ))}
                </div>
              </div>
              {urgencyInfo && (
                <span className={`text-xs font-semibold px-4 py-1.5 rounded-full whitespace-nowrap ${urgencyInfo.color}`}>
                  {urgencyInfo.label}
                </span>
              )}
            </div>
          )}

          {submitted ? (
            <div className="bg-white rounded-2xl p-12 text-center">
              <div className="w-20 h-20 flex items-center justify-center rounded-full bg-green-100 mx-auto mb-5">
                <i className="ri-checkbox-circle-fill text-green-500 text-4xl"></i>
              </div>
              <h2 className="text-2xl font-extrabold text-[#1E3A5F] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                Request Submitted!
              </h2>
              <p className="text-gray-500 text-sm mb-2">
                Thank you for reaching out to Mitigation Restoration Services.
              </p>
              <p className="text-gray-500 text-sm mb-8">
                Our team will review your submission and contact you shortly. For emergencies, please call us directly.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="tel:+18005551234"
                  className="inline-flex items-center justify-center gap-2 bg-[#DC2626] text-white px-8 py-3.5 rounded-full font-semibold text-sm cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-phone-fill"></i>
                  Call Us Now
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 border-2 border-[#1E3A5F] text-[#1E3A5F] px-8 py-3.5 rounded-full font-semibold text-sm cursor-pointer whitespace-nowrap"
                >
                  Back to Home
                </Link>
              </div>
            </div>
          ) : (
            <form
              data-readdy-form
              id="customer-info-form"
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl p-8 space-y-6"
            >
              {/* Contact Info */}
              <div>
                <h2 className="font-bold text-[#1E3A5F] text-base mb-4 flex items-center gap-2">
                  <i className="ri-user-fill text-[#DC2626]"></i>
                  Contact Information
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Full Name *</label>
                    <input
                      type="text"
                      name="full_name"
                      required
                      placeholder="John Smith"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1E3A5F] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="john@example.com"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1E3A5F] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      placeholder="+1 (555) 000-0000"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1E3A5F] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Best Time to Call</label>
                    <select
                      name="best_time_to_call"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#1E3A5F] transition-colors cursor-pointer"
                    >
                      <option value="">Select a time</option>
                      <option value="Morning (8AM–12PM)">Morning (8AM–12PM)</option>
                      <option value="Afternoon (12PM–5PM)">Afternoon (12PM–5PM)</option>
                      <option value="Evening (5PM–8PM)">Evening (5PM–8PM)</option>
                      <option value="Anytime">Anytime</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Property Info */}
              <div>
                <h2 className="font-bold text-[#1E3A5F] text-base mb-4 flex items-center gap-2">
                  <i className="ri-home-fill text-[#DC2626]"></i>
                  Property Information
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Property Address *</label>
                    <input
                      type="text"
                      name="property_address"
                      required
                      placeholder="123 Main St, Houston, TX 77001"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1E3A5F] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Property Type</label>
                    <select
                      name="property_type"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#1E3A5F] transition-colors cursor-pointer"
                    >
                      <option value="">Select type</option>
                      <option value="Residential Home">Residential Home</option>
                      <option value="Apartment / Condo">Apartment / Condo</option>
                      <option value="Commercial Building">Commercial Building</option>
                      <option value="Industrial / Warehouse">Industrial / Warehouse</option>
                      <option value="Multi-Family">Multi-Family</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Approximate Affected Area</label>
                    <select
                      name="affected_area"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#1E3A5F] transition-colors cursor-pointer"
                    >
                      <option value="">Select size</option>
                      <option value="Small (1 room)">Small (1 room)</option>
                      <option value="Medium (2–4 rooms)">Medium (2–4 rooms)</option>
                      <option value="Large (5+ rooms)">Large (5+ rooms)</option>
                      <option value="Entire property">Entire property</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Loss Description */}
              <div>
                <h2 className="font-bold text-[#1E3A5F] text-base mb-4 flex items-center gap-2">
                  <i className="ri-file-text-fill text-[#DC2626]"></i>
                  Describe Your Loss
                </h2>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                    Description of Damage *
                    <span className={`ml-2 font-normal ${charCount > 500 ? 'text-red-500' : 'text-gray-400'}`}>
                      {charCount}/500
                    </span>
                  </label>
                  <textarea
                    name="loss_description"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="Please describe what happened, when it occurred, and the current condition of your property. Include any details that may help our team prepare..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1E3A5F] transition-colors resize-none"
                  ></textarea>
                </div>
                <div className="mt-4">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">When Did the Damage Occur?</label>
                  <input
                    type="date"
                    name="damage_date"
                    className="w-full sm:w-64 border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#1E3A5F] transition-colors"
                  />
                </div>
              </div>

              {/* Photo / Video Upload */}
              <div>
                <h2 className="font-bold text-[#1E3A5F] text-base mb-2 flex items-center gap-2">
                  <i className="ri-image-fill text-[#DC2626]"></i>
                  Upload Photos & Videos
                </h2>
                <p className="text-xs text-gray-400 mb-4">
                  Upload images or videos of the damage to help us assess the situation before arrival. Accepted: JPG, PNG, MP4, MOV (max 10 files)
                </p>

                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-[#1E3A5F] hover:bg-[#F8F9FA] transition-colors"
                >
                  <i className="ri-upload-cloud-2-fill text-gray-300 text-4xl mb-3 block"></i>
                  <p className="text-sm font-semibold text-gray-600">Click to upload photos or videos</p>
                  <p className="text-xs text-gray-400 mt-1">JPG, PNG, MP4, MOV — up to 10 files</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    name="media_files"
                    accept="image/*,video/*"
                    multiple
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </div>

                {filePreviews.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {filePreviews.map((file, idx) => (
                      <div key={idx} className="relative bg-[#F8F9FA] rounded-xl p-3 flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#1E3A5F]/10 shrink-0">
                          <i className={`${file.isVideo ? 'ri-video-fill' : 'ri-image-fill'} text-[#1E3A5F] text-base`}></i>
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-gray-700 truncate">{file.name}</p>
                          <p className="text-xs text-gray-400">{file.size}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(idx)}
                          className="absolute top-2 right-2 w-5 h-5 flex items-center justify-center rounded-full bg-gray-200 hover:bg-red-100 cursor-pointer"
                        >
                          <i className="ri-close-line text-gray-500 text-xs"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Insurance */}
              <div>
                <h2 className="font-bold text-[#1E3A5F] text-base mb-4 flex items-center gap-2">
                  <i className="ri-shield-fill text-[#DC2626]"></i>
                  Insurance Information <span className="text-gray-400 font-normal text-xs">(Optional)</span>
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Insurance Company</label>
                    <input
                      type="text"
                      name="insurance_company"
                      placeholder="e.g. State Farm, Allstate"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1E3A5F] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Claim Number</label>
                    <input
                      type="text"
                      name="claim_number"
                      placeholder="If already filed"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1E3A5F] transition-colors"
                    />
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={submitting || charCount > 500}
                  className="w-full bg-[#DC2626] text-white py-4 rounded-xl font-semibold text-sm hover:bg-red-700 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {submitting ? 'Submitting...' : 'Submit Service Request'}
                </button>
                <p className="text-center text-xs text-gray-400 mt-3">
                  For emergencies, call directly:{' '}
                  <a href="tel:+18005551234" className="text-[#DC2626] font-semibold">+1 (800) 555-1234</a>
                </p>
              </div>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
